import { Stack, FormControlLabel, Switch } from '@mui/material';
import { ChangeEvent } from 'react';
import { useRecoilState } from 'recoil';
import { MHMTypography, DisclaimerText } from '../MHM/MHMTypography';
import { useTranslation } from '../../../contexts/TranslationContext';
import { changeUserAtom } from '../../../models/atoms/account_atoms';
import type { AlertTypes } from '../../../models/interfaces/account_interfaces';

const ALERT_TYPES: { type: AlertTypes; title: string; description: string }[] =
  [
    {
      type: 'water_level',
      title: 'Water Level Alerts',
      description: 'Enable alerts for high warning level and high ROC',
    },
    {
      type: 'measurement',
      title: 'Measurement Alerts',
      description: 'Enable alerts for missed measurements',
    },
    {
      type: 'analytics',
      title: 'Analytics Alerts',
      description:
        'Enable alerts for predictions for blockages, floods, silt build-ups, and infiltration',
    },
    {
      type: 'low_battery',
      title: 'Low Battery Alerts',
      description: 'Enable alerts for low battery devices',
    },
    {
      type: 'weather_mode',
      title: 'Weather Mode Alerts',
      description: 'Enable alerts for device weather mode activations',
    },
  ] as const;

export function AlertTypeSettings() {
  const [changeUser, setChangeUser] = useRecoilState(changeUserAtom);
  const { t } = useTranslation();

  function changeAlertTypes(type: AlertTypes) {
    return (e: ChangeEvent<HTMLInputElement>) => {
      if (!changeUser) {
        return;
      }

      setChangeUser(prev =>
        prev
          ? {
              ...prev,
              change_alert_types: true,
              alerts: { ...prev.alerts, [type]: e.target.checked },
            }
          : prev,
      );
    };
  }

  if (!changeUser || (!changeUser.email_alerts && !changeUser.text_alerts)) {
    return null;
  }

  return (
    <Stack gap={1}>
      {ALERT_TYPES.map(({ type, title, description }) => (
        <FormControlLabel
          key={type}
          control={
            <Switch
              checked={changeUser.alerts[type]}
              onChange={changeAlertTypes(type)}
            />
          }
          label={
            <>
              <MHMTypography medium>{t(title)}</MHMTypography>
              <DisclaimerText fontSize="small">{t(description)}</DisclaimerText>
            </>
          }
        />
      ))}
    </Stack>
  );
}
